
import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'

const USER_ID = '00000000-0000-0000-0000-000000000000'

/**
 * Hook for managing column order with Supabase persistence (cross-device)
 * and localStorage as a fast cache.
 * Returns the ordered column IDs and functions to move columns.
 */
export function useColumnOrder(storageKey: string, defaultColumns: string[]) {
  const localStorageKey = `tradingApp_colOrder_${storageKey}`
  const supabaseKey = `colOrder_${storageKey}`
  const isMounted = useRef(true)

  const getLocalOrder = (): string[] => {
    try {
      const stored = localStorage.getItem(localStorageKey)
      if (stored) {
        const parsed = JSON.parse(stored) as string[]
        const defaultSet = new Set(defaultColumns)
        const validStored = parsed.filter(c => defaultSet.has(c))
        const missing = defaultColumns.filter(c => !parsed.includes(c))
        if (validStored.length > 0) return [...validStored, ...missing]
      }
    } catch { /* ignore */ }
    return defaultColumns
  }

  const [columnOrder, setColumnOrder] = useState<string[]>(getLocalOrder)

  // On mount, fetch from Supabase and override if newer
  useEffect(() => {
    isMounted.current = true
    const fetchFromSupabase = async () => {
      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('preference_value')
          .eq('user_id', USER_ID)
          .eq('preference_key', supabaseKey)
          .single()

        if (error || !data) return

        const remoteOrder = data.preference_value as string[]
        if (Array.isArray(remoteOrder) && remoteOrder.length > 0) {
          const defaultSet = new Set(defaultColumns)
          const validRemote = remoteOrder.filter(c => defaultSet.has(c))
          const missing = defaultColumns.filter(c => !remoteOrder.includes(c))
          if (validRemote.length > 0 && isMounted.current) {
            const merged = [...validRemote, ...missing]
            setColumnOrder(merged)
            localStorage.setItem(localStorageKey, JSON.stringify(merged))
          }
        }
      } catch { /* ignore — use localStorage fallback */ }
    }
    fetchFromSupabase()
    return () => { isMounted.current = false }
  }, [supabaseKey])

  const saveOrder = useCallback((newOrder: string[]) => {
    setColumnOrder(newOrder)
    // Save to localStorage immediately (fast)
    localStorage.setItem(localStorageKey, JSON.stringify(newOrder))
    // Save to Supabase async (cross-device persistence)
    supabase
      .from('user_preferences')
      .upsert(
        { user_id: USER_ID, preference_key: supabaseKey, preference_value: newOrder },
        { onConflict: 'user_id,preference_key' }
      )
      .then(() => {})
  }, [localStorageKey, supabaseKey])

  const moveColumn = useCallback((fromIndex: number, toIndex: number) => {
    const newOrder = [...columnOrder]
    const [moved] = newOrder.splice(fromIndex, 1)
    newOrder.splice(toIndex, 0, moved)
    saveOrder(newOrder)
  }, [columnOrder, saveOrder])

  const resetOrder = useCallback(() => {
    saveOrder(defaultColumns)
  }, [defaultColumns, saveOrder])

  // Drag-and-drop state
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const dragHandlers = useCallback((index: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      setDragIndex(index)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.style.opacity = '0.4'
      }
    },
    onDragEnd: (e: React.DragEvent) => {
      setDragIndex(null)
      setDragOverIndex(null)
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.style.opacity = '1'
      }
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverIndex(index)
    },
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault()
      setDragOverIndex(index)
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      if (dragIndex !== null && dragIndex !== index) {
        moveColumn(dragIndex, index)
      }
      setDragIndex(null)
      setDragOverIndex(null)
    },
    style: {
      cursor: 'grab',
      borderLeft: dragOverIndex === index && dragIndex !== null && dragIndex !== index
        ? '3px solid var(--color-accent)'
        : undefined,
      opacity: dragIndex === index ? 0.4 : undefined,
    } as React.CSSProperties,
  }), [dragIndex, dragOverIndex, moveColumn])

  return {
    columnOrder,
    moveColumn,
    resetOrder,
    dragHandlers,
    isDragging: dragIndex !== null,
  }
}
